import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Card } from "@/design-system/components/ui";
import { useAggregateConcentration } from "@/hooks/capitalMarkets/useDashboard";
import type {
  ConcentrationSlice,
} from "@/types/capitalMarketsDashboard";
import { formatCurrency, formatPct } from "../shared/format";

// Token-derived donut palette — references the CSS custom properties the
// design tokens expose, so no raw hex appears here.
const DONUT_COLORS = [
  "var(--gold)",
  "var(--status-info)",
  "var(--status-success)",
  "var(--status-warning)",
  "var(--gray-mid)",
  "var(--gold-shadow)",
];

export interface ConcentrationSelection {
  dimensionLabel: string;
  key: string;
}

interface Props {
  onSliceClick: (selection: ConcentrationSelection) => void;
}

/**
 * SPV Pool v1.0 §4.7 — aggregate concentration across all active pools as a
 * 2×2 grid of donut charts. Clicking a slice cross-filters the Pools In
 * Flight table.
 */
export function AggregateConcentrationGrid({ onSliceClick }: Props) {
  const query = useAggregateConcentration();
  const data = query.data;

  return (
    <Card>
      <h2 className="font-display font-bold text-h3 m-0 mb-space-3">
        Aggregate Concentration
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-4">
        <Donut
          title="Borrower"
          slices={data?.byBorrower ?? []}
          loading={query.isLoading}
          onSliceClick={(key) => onSliceClick({ dimensionLabel: "Borrower", key })}
        />
        <Donut
          title="Credit Rating"
          slices={data?.byCreditRating ?? []}
          loading={query.isLoading}
          onSliceClick={(key) =>
            onSliceClick({ dimensionLabel: "Credit Rating", key })
          }
        />
        <Donut
          title="Industry"
          slices={data?.byIndustry ?? []}
          loading={query.isLoading}
          emptyHint="Industry is not yet on the loan contract."
          onSliceClick={(key) => onSliceClick({ dimensionLabel: "Industry", key })}
        />
        <Donut
          title="Geography"
          slices={data?.byGeography ?? []}
          loading={query.isLoading}
          emptyHint="Geography is not yet on the loan contract."
          onSliceClick={(key) =>
            onSliceClick({ dimensionLabel: "Geography", key })
          }
        />
      </div>
    </Card>
  );
}

function Donut({
  title,
  slices,
  loading,
  emptyHint,
  onSliceClick,
}: {
  title: string;
  slices: ConcentrationSlice[];
  loading: boolean;
  emptyHint?: string;
  onSliceClick: (key: string) => void;
}) {
  return (
    <div data-testid={`concentration-donut-${title}`}>
      <h3 className="font-display font-bold text-body m-0 mb-space-2">
        {title}
      </h3>
      {loading ? (
        <p className="text-small text-gray-mid italic m-0">Loading…</p>
      ) : slices.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          {emptyHint ?? "No concentration data yet."}
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie
                data={slices}
                dataKey="contributedPrincipal"
                nameKey="key"
                innerRadius={40}
                outerRadius={66}
              >
                {slices.map((s, i) => (
                  <Cell
                    key={s.key}
                    fill={DONUT_COLORS[i % DONUT_COLORS.length]}
                    cursor="pointer"
                    onClick={() => onSliceClick(s.key)}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
            </PieChart>
          </ResponsiveContainer>
          <ul className="m-0 p-0 list-none flex flex-col gap-[2px]">
            {slices.slice(0, 4).map((s) => (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => onSliceClick(s.key)}
                  className="w-full flex items-center justify-between gap-space-2 text-caption text-onyx hover:text-gold-shadow"
                >
                  <span className="truncate">{s.key}</span>
                  <span className="text-gray-mid shrink-0">
                    {formatPct(s.pct)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
